import type { AgentBillingPlan } from "schemas/agent-billing";

import { exhaustiveSwitch } from "replo-utils/lib/misc";

type PlanChangeType = "upgrade" | "downgrade" | "change-plan" | "resume-plan";

const PLAN_RANK: Record<AgentBillingPlan, number> = {
  starter: 1,
  pro: 2,
};

export function getPlanChangeType({
  currentPlan,
  nextPlan,
  isPlanCanceled,
}: {
  currentPlan: AgentBillingPlan | null;
  nextPlan: AgentBillingPlan | null;
  isPlanCanceled: boolean;
}): PlanChangeType {
  if (isPlanCanceled) {
    return "resume-plan";
  }
  if (currentPlan === null || currentPlan !== nextPlan) {
    if (currentPlan === null) {
      return "upgrade";
    }
    if (nextPlan === null) {
      return "downgrade";
    }
    return PLAN_RANK[nextPlan] > PLAN_RANK[currentPlan]
      ? "upgrade"
      : "downgrade";
  }
  return "change-plan";
}

export function getPlanChangeCtaButtonLabel({
  currentPlan,
  nextPlan,
  planName,
  isPlanCanceled,
  isIntervalUpgrade,
  hasChanges = true,
}: {
  currentPlan: AgentBillingPlan | null;
  nextPlan: AgentBillingPlan;
  planName: string;
  isPlanCanceled: boolean;
  isIntervalUpgrade: boolean;
  hasChanges?: boolean;
}): {
  buttonLabel: string;
  buttonVariant: "primary" | "secondary";
  isNoOp: boolean;
} {
  const changeType = getPlanChangeType({
    currentPlan,
    nextPlan,
    isPlanCanceled,
  });

  if (changeType === "change-plan" && !hasChanges) {
    return {
      buttonLabel: "Current Plan",
      buttonVariant: "secondary",
      isNoOp: true,
    };
  }

  if (changeType === "change-plan" && isIntervalUpgrade) {
    return {
      buttonLabel: "Update to Annual",
      buttonVariant: "primary",
      isNoOp: false,
    };
  }

  const result = exhaustiveSwitch({ type: changeType })({
    upgrade: {
      buttonLabel: `Upgrade to ${planName}`,
      buttonVariant: "primary" as const,
    },
    downgrade: {
      buttonLabel: `Downgrade to ${planName}`,
      buttonVariant: "secondary" as const,
    },
    "change-plan": {
      buttonLabel: "Update Plan",
      buttonVariant: "primary" as const,
    },
    "resume-plan": {
      buttonLabel: "Resume Your Plan",
      buttonVariant: "primary" as const,
    },
  });

  return { ...result, isNoOp: false };
}
